import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  CircularProgress,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Send as SubmitIcon } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { keystoneService, Modality, ImagingRequest } from '../../services/keystoneService';

/**
 * Create OR edit a Keystone imaging request DRAFT.
 *
 * Two modes:
 *   - Create (default): no `editTarget` → POST /keystone/imaging-requests.
 *     "Save as draft" leaves status DRAFT; "Save & submit" pushes MWL.
 *   - Edit: `editTarget` set to an existing DRAFT row → PATCH the row.
 *     Submitting from edit mode also flips DRAFT → SUBMITTED in one go.
 *     SUBMITTED rows should never reach this dialog — the parent gates the
 *     edit affordance on `submissionStatus === 'DRAFT'`.
 */
interface Props {
  open: boolean;
  horseId: string;
  /** When set, dialog is in edit mode. Form prefills from this row. */
  editTarget?: ImagingRequest | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormState {
  modalityId: number | '';
  bodyRegion: string;
  clinicalHistory: string;
  priority: string;
  notes: string;
  /**
   * Date object representing the picker's currently-selected wallclock.
   * Stored as a Date for the MUI DateTimePicker API; serialized to a naive
   * local string ("YYYY-MM-DDTHH:mm:ss", no Z) at save time. The backend
   * persists the naive string onto ImagingRequest.requestedDateTime and
   * re-formats at submit time using the clinic's IANA timezone
   * (see imaging-requests.service.ts formatNaiveLocalTime) — so we
   * deliberately don't do any TZ math here.
   */
  requestedDateTime: Date | null;
}

const buildEmptyForm = (): FormState => ({
  modalityId: '',
  bodyRegion: '',
  clinicalHistory: '',
  priority: 'Routine',
  notes: '',
  requestedDateTime: defaultPickerDate(),
});

/**
 * Default picker value — the next round hour from now. Avoids a blank
 * field and saves the clinician a few clicks for the common
 * "schedule for later today" case.
 */
function defaultPickerDate(): Date {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
}

/**
 * Format a Date as a naive local-wallclock string the backend's submit
 * path expects (preserves the clinician's intent regardless of where the
 * server lives — Render runs in UTC). Mirrors the helper from the prior
 * ImagingRequestDialog implementation; the format is what asteris.service
 * eventually emits in the SOAP envelope after clinic-tz formatting.
 */
function formatNaiveLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

/** Parse a backend-stored ISO string back into a Date for the picker. */
function parseIso(iso: string | undefined | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

const NewImagingRequestDialog: React.FC<Props> = ({
  open,
  horseId,
  editTarget,
  onClose,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState<FormState>(buildEmptyForm);
  const [saving, setSaving] = useState(false);
  const isEdit = !!editTarget;

  const { data: modalities = [] } = useQuery<Modality[]>(
    ['keystone-modalities'],
    () => keystoneService.getModalities(),
    { staleTime: 5 * 60 * 1000, enabled: open },
  );

  // Sync form to editTarget on open (in edit mode) or reset to empty.
  // Re-runs whenever the dialog opens so a re-edit picks up the latest row
  // values rather than a stale snapshot from the previous open.
  useEffect(() => {
    if (!open) return;
    if (editTarget) {
      setForm({
        modalityId: editTarget.modalityId ?? '',
        bodyRegion: editTarget.bodyRegion ?? '',
        clinicalHistory: editTarget.clinicalHistory ?? '',
        priority: editTarget.priority ?? 'Routine',
        notes: editTarget.notes ?? '',
        requestedDateTime: parseIso(editTarget.requestedDateTime) ?? defaultPickerDate(),
      });
    } else {
      setForm(buildEmptyForm());
    }
  }, [open, editTarget]);

  const handleClose = () => {
    setSaving(false);
    setForm(buildEmptyForm());
    onClose();
  };

  const save = async (submitImmediately: boolean) => {
    if (!form.modalityId) {
      enqueueSnackbar('Modality is required', { variant: 'warning' });
      return;
    }
    if (!form.requestedDateTime) {
      enqueueSnackbar('Requested date & time is required', { variant: 'warning' });
      return;
    }
    const chosen = modalities.find(m => m.modalityId === Number(form.modalityId));
    const requestedDateTime = formatNaiveLocal(form.requestedDateTime);
    setSaving(true);
    try {
      if (isEdit && editTarget) {
        // PATCH the existing DRAFT first, then optionally submit.
        await keystoneService.updateImagingRequest(editTarget.id, {
          modalityId: Number(form.modalityId),
          modalityName: chosen?.modalityName,
          bodyRegion: form.bodyRegion || undefined,
          clinicalHistory: form.clinicalHistory || undefined,
          priority: form.priority || undefined,
          notes: form.notes || undefined,
          requestedDateTime,
        });
        if (submitImmediately) {
          await keystoneService.submitImagingRequest(editTarget.id, {
            modalityId: Number(form.modalityId),
            scheduleModalityId: chosen?.scheduleModalityId,
          });
          enqueueSnackbar('Updated and submitted to Keystone', { variant: 'success' });
        } else {
          enqueueSnackbar('Draft updated', { variant: 'success' });
        }
      } else {
        await keystoneService.createImagingRequest({
          horseId,
          modalityId: Number(form.modalityId),
          modalityName: chosen?.modalityName,
          bodyRegion: form.bodyRegion || undefined,
          clinicalHistory: form.clinicalHistory || undefined,
          priority: form.priority || undefined,
          notes: form.notes || undefined,
          requestedDateTime,
          submitImmediately,
        });
        enqueueSnackbar(submitImmediately ? 'Submitted to Keystone' : 'Draft saved', {
          variant: 'success',
        });
      }
      onSuccess();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Save failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Imaging Request' : 'New Imaging Request'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={0.5}>
          <TextField
            select
            size="small"
            label="Modality *"
            value={form.modalityId}
            onChange={e => setForm(f => ({ ...f, modalityId: Number(e.target.value) }))}
          >
            {modalities.map(m => (
              <MenuItem key={m.modalityId} value={m.modalityId}>
                {m.modalityName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Body region / location"
            value={form.bodyRegion}
            onChange={e => setForm(f => ({ ...f, bodyRegion: e.target.value }))}
            placeholder="e.g. Left front fetlock"
          />
          <DateTimePicker
            label="Requested date & time *"
            value={form.requestedDateTime}
            onChange={newValue =>
              setForm(f => ({ ...f, requestedDateTime: newValue }))
            }
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                helperText:
                  'Time the study should be scheduled for on the modality.',
              },
            }}
          />
          <TextField
            size="small"
            label="Clinical history"
            multiline
            rows={2}
            value={form.clinicalHistory}
            onChange={e => setForm(f => ({ ...f, clinicalHistory: e.target.value }))}
          />
          <TextField
            select
            size="small"
            label="Priority"
            value={form.priority}
            onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
          >
            {['Routine', 'Urgent', 'STAT'].map(p => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Notes"
            multiline
            rows={2}
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="outlined"
          onClick={() => save(false)}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
        >
          {isEdit ? 'Save changes' : 'Save as draft'}
        </Button>
        <Button
          variant="contained"
          onClick={() => save(true)}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} /> : <SubmitIcon />}
        >
          {isEdit ? 'Save & submit' : 'Save & submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewImagingRequestDialog;
